// DLC_09 · e-Doręczenia — warstwa API. Kolejka wysyłki przyjmuje multipart (załączniki),
// akcje na wpływie idą JSON-em. Skrzynka odbiorcza jest po stronie backendu zamknięta dla
// wszystkich poza CEO — front tylko nie pokazuje tych ekranów pozostałym.
import apiClient from "@/api/client";
import type {
  EdoreczeniaSummary,
  IncomingMessage,
  Mailbox,
  MailboxOption,
  OutgoingMessage,
  RecipientOption,
} from "./types";

const BASE = "/edoreczenia";
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

// ── podsumowanie modułu ──────────────────────────────────────────────────────
export const getSummary = () => unwrap<EdoreczeniaSummary>(apiClient.get(`${BASE}/summary/`));

export const getRecipients = (q?: string) =>
  unwrap<RecipientOption[]>(apiClient.get(`${BASE}/recipients/`, { params: q ? { q } : {} }));

// ── kolejka wysyłki ──────────────────────────────────────────────────────────
export const getOutbox = (params?: Record<string, string>) =>
  unwrap<OutgoingMessage[]>(
    apiClient.get(`${BASE}/outbox/`, { params: { page_size: 500, ...params } }),
  );

export const getOutboxCounts = () =>
  unwrap<{ queued: number; approved: number; failed: number; rejected: number }>(
    apiClient.get(`${BASE}/outbox/counts/`),
  );

export const queueMessage = (fd: FormData) =>
  unwrap<OutgoingMessage>(apiClient.post(`${BASE}/outbox/`, fd, MULTIPART));

export const updateQueuedMessage = (id: string, fd: FormData) =>
  unwrap<OutgoingMessage>(apiClient.patch(`${BASE}/outbox/${id}/`, fd, MULTIPART));

const outboxAction = (id: string, action: string, body?: unknown) =>
  unwrap<OutgoingMessage>(apiClient.post(`${BASE}/outbox/${id}/${action}/`, body ?? {}));

export const approveMessage = (id: string) => outboxAction(id, "approve");
export const rejectMessage = (id: string, reason: string) => outboxAction(id, "reject", { reason });
export const cancelMessage = (id: string) => outboxAction(id, "cancel");
export const sendMessage = (id: string) => outboxAction(id, "send");
export const refreshMessage = (id: string) => outboxAction(id, "refresh");

// ── skrzynka odbiorcza (CEO) ─────────────────────────────────────────────────
export const getInbox = (params?: Record<string, string>) =>
  unwrap<IncomingMessage[]>(
    apiClient.get(`${BASE}/inbox/`, { params: { page_size: 500, ...params } }),
  );

export const syncInbox = () =>
  unwrap<{ new_messages: number }>(apiClient.post(`${BASE}/inbox/sync/`, {}));

// „Odbierz" — pobranie treści i załączników. To jest akt odbioru: uruchamia terminy,
// dlatego nigdy nie dzieje się automatycznie.
export const fetchIncoming = (id: string) =>
  unwrap<IncomingMessage>(apiClient.post(`${BASE}/inbox/${id}/fetch/`, {}));

export const fileIncomingToRegister = (
  id: string,
  payload: { target_users?: string[]; target_project?: string | null; note?: string },
) => unwrap<IncomingMessage>(apiClient.post(`${BASE}/inbox/${id}/file-to-register/`, payload));

export const handleIncomingExternally = (id: string, note: string) =>
  unwrap<IncomingMessage>(apiClient.post(`${BASE}/inbox/${id}/handle-externally/`, { note }));

// Wyszukanie adresu do e-Doręczeń w rejestrze operatora. Zwraca available=false, dopóki
// nie mamy dostępu do wyszukiwarki — wtedy front prosi o ręczne wpisanie adresu.
export const lookupEdeliveryAddress = async (query: string) => {
  const r = await apiClient.get(`${BASE}/address-lookup/`, { params: { q: query } });
  const body = r.data as {
    data: { edelivery_address: string; name: string; nip: string; address: string }[];
    meta?: { available?: boolean; reason?: string };
  };
  return {
    results: body.data ?? [],
    available: body.meta?.available !== false,
    reason: body.meta?.reason ?? "",
  };
};

// Skrzynki do wyboru w formularzu kolejki — dostępne dla każdego z modułem
// (konfiguracja poniżej jest zamknięta dla CEO).
export const getMailboxOptions = () =>
  unwrap<MailboxOption[]>(apiClient.get(`${BASE}/mailbox-options/`));

// ── skrzynki (konfiguracja, CEO) ─────────────────────────────────────────────
export const getMailboxes = () => unwrap<Mailbox[]>(apiClient.get(`${BASE}/mailboxes/`));

export const createMailbox = (payload: Record<string, unknown>) =>
  unwrap<Mailbox>(apiClient.post(`${BASE}/mailboxes/`, payload));

export const updateMailbox = (id: string, payload: Record<string, unknown>) =>
  unwrap<Mailbox>(apiClient.patch(`${BASE}/mailboxes/${id}/`, payload));

export const testMailboxConnection = (id: string) =>
  unwrap<{ ok: boolean; error: string; state: string }>(
    apiClient.post(`${BASE}/mailboxes/${id}/test-connection/`, {}),
  );

// Wniosek o certyfikat (CSR) — plik do wgrania przez administratora skrzynki w module
// uprawnień operatora. Zwracamy blob, bo to plik do pobrania, nie JSON.
export const downloadMailboxCsr = async (id: string, confirmReplace = false) => {
  const r = await apiClient.post(
    `${BASE}/mailboxes/${id}/csr/`,
    { confirm_replace: confirmReplace },
    { responseType: "blob" },
  );
  return r.data as Blob;
};

export const uploadMailboxCertificate = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<Mailbox>(apiClient.post(`${BASE}/mailboxes/${id}/certificate/`, fd, MULTIPART));
};
